import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import sha1 from 'sha1';
import { ConfigContext, UserContext } from '../../../../../context';
import { useAnalytics } from '../../../../hooks';
import { getCssVar, getDeepLinkUrl } from '../../../../utils';
import { Deeplink, DeeplinkType } from '../../../Deeplink';
import { IconLabel } from '../../../IconLabel';
import { Modal } from '../../../Modal';
import { ShareUrlViaSocial } from '../../../ShareUrlViaSocial';

import classes from './EventShare.module.scss';

interface EventShareProps {
  eventId: number;
  className?: string;
}

export const EventShare: FunctionComponent<EventShareProps> = memo(({ eventId, className }) => {
  const { t } = useTranslation();

  const { id: userId, channelId } = useContext(UserContext).userInfo.userData;

  const { deeplinkEnabled = true } = {
    ...useContext(ConfigContext).config.elements.sharePost,
  };

  const { logEventShared } = useAnalytics();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const logShare = useCallback(() => {
    logEventShared({
      user_Id: sha1(`${userId}`),
      channel_id: channelId,
      event_id: eventId,
      time_stamp: new Date().toISOString(),
    });
  }, [channelId, eventId, logEventShared, userId]);

  const deeplinkClick = useCallback(() => {
    setIsOpen(false);
    logShare();
  }, [logShare]);

  const title = t('eventShare.title');

  const body = useMemo(() => {
    return (
      <div className={classes['event-share']}>
        <div className={classes['event-share__title']}>{title}</div>

        <ShareUrlViaSocial
          url={getDeepLinkUrl({ type: DeeplinkType.EVENT, channelId, eventId })}
          subject={t('eventShare.email-subject')}
          body={`${t('eventShare.email-body')}\n`}
        />

        {deeplinkEnabled && (
          <Deeplink
            type={DeeplinkType.EVENT}
            eventId={eventId}
            label={t('eventActions.deeplink')}
            onClick={deeplinkClick}
            showAsButton
          />
        )}
      </div>
    );
  }, [channelId, deeplinkClick, deeplinkEnabled, eventId, t, title]);

  return (
    <>
      <IconLabel
        className={className}
        label={t('eventActions.share')}
        iconId={'share'}
        iconSize={18}
        color={getCssVar('--action-pane-icon-color')}
        hoverColor={getCssVar('--action-pane-icon-hover-color')}
        onClick={() => setIsOpen(true)}
      />

      <Modal
        isOpen={isOpen}
        body={body}
        onClose={() => setIsOpen(false)}
        contentStyle={{ width: '26rem' }}
        ariaLabel={title}
      />
    </>
  );
});
